import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  FunnelIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';
import useSubmissionProtection from '../hooks/useSubmissionProtection';
import { 
  Input, 
  Select, 
  Button, 
  Modal, 
  DataTable, 
  showToast,
  ConfirmDialog,
  DatePicker,
  DateRangePicker
} from '../components/UI';
import Badge from '../components/UI/Badge';
import LocationInput from '../components/UI/LocationInput';
import LocationDisplay from '../components/UI/LocationDisplay';
import enquiryService, { Enquiry, EnquiryFormData } from '../services/enquiryService';
import { userService } from '../services/userService';
import { rigService } from '../services/rigService';
import { boreSizeService } from '../services/boreSizeService';
import api from '../services/api';

interface TableParams {
  page: number;
  limit: number;
  search: string;
  status: string;
  userId: string;
  boreSizeId: string;
  createdById: string;
  updatedById: string;
  serviceDateFrom: string | null;
  serviceDateTo: string | null;
  enquiryDateFrom: string | null;
  enquiryDateTo: string | null;
}

interface EnquiryFormDataWithEdit extends EnquiryFormData {
  isEdit?: boolean;
}

// Validation schema
const enquirySchema = yup.object().shape({
  company_name: yup.string().optional(),
  contact_person: yup.string().required('Contact person is required'),
  street: yup.string().optional(),
  town: yup.string().optional(),
  district: yup.string().optional(),
  contact_no: yup.string()
    .required('Mobile number is required')
    .matches(/^[0-9+\-\s()]*$/, 'Invalid phone number format'),
  service_date: yup.string().required('Service date is required'),
  bore_size_id: yup.number().required('Bore size is required'),
  pipe_rate: yup.mixed().optional(),
  pipe_rate_alt: yup.mixed().optional(),
  batta: yup.mixed().optional(),
  user_id: yup.number().when('isEdit', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.required('Assign manager is required')
  }),
  assign_field_team: yup.number().optional(),
  rig_id: yup.number().optional(),
  location_coordinates: yup.string().optional()
    .test('valid-coordinates', 'Invalid coordinates format', function(value) {
      if (!value) return true;
      const pattern = /^-?\d+\.?\d*,-?\d+\.?\d*$/;
      return pattern.test(value);
    }),
  location_url: yup.string().optional()
    .test('valid-url', 'Must be a valid Google Maps URL', function(value) {
      if (!value) return true;
      return value.includes('maps.google.com') ||
             value.includes('goo.gl') ||
             value.includes('maps.app.goo.gl') ||
             value.includes('google.com/maps');
    }),
  isEdit: yup.boolean().optional()
});

const EnquiriesManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState<Enquiry | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; enquiry: Enquiry | null }>({
    show: false,
    enquiry: null
  });
  
  // Lists for dropdowns
  const [users, setUsers] = useState<any[]>([]);
  const [fieldTeamUsers, setFieldTeamUsers] = useState<any[]>([]);
  const [boreSizes, setBoreSizes] = useState<any[]>([]);
  const [rigs, setRigs] = useState<any[]>([]);
  
  // Table state
  const [tableParams, setTableParams] = useState<TableParams>({
    page: 1,
    limit: 15, // Increased default from 10 to 15 for better mobile experience
    search: '',
    status: '',
    userId: '',
    boreSizeId: '',
    createdById: '',
    updatedById: '',
    serviceDateFrom: null,
    serviceDateTo: null,
    enquiryDateFrom: null,
    enquiryDateTo: null
  });
  const [totalEnquiries, setTotalEnquiries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true }
  ]);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);
  
  // Date range state
  const [, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showDesktopFilters, setShowDesktopFilters] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [showDetailSidebar, setShowDetailSidebar] = useState(false);

  // Temporary filter values (only applied when user clicks "Apply Filters")
  const [tempFilters, setTempFilters] = useState({
    status: '',
    userId: '',
    boreSizeId: '',
    createdById: '',
    updatedById: '',
    serviceDateFrom: null as Date | null,
    serviceDateTo: null as Date | null,
    enquiryDateFrom: null as Date | null,
    enquiryDateTo: null as Date | null
  });
  
  const requestIdRef = useRef(0);
  const { protectedSubmit } = useSubmissionProtection();

  // Helper function to apply filters
  const applyFilters = () => {
    // Format dates in local timezone to avoid timezone conversion issues
    const formatLocalDate = (date: Date | null) => {
      if (!date) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setTableParams(prev => ({
      ...prev,
      status: tempFilters.status,
      userId: tempFilters.userId,
      boreSizeId: tempFilters.boreSizeId,
      createdById: tempFilters.createdById,
      updatedById: tempFilters.updatedById,
      serviceDateFrom: tempFilters.serviceDateFrom ? formatLocalDate(tempFilters.serviceDateFrom) : null,
      serviceDateTo: tempFilters.serviceDateTo ? formatLocalDate(tempFilters.serviceDateTo) : null,
      enquiryDateFrom: tempFilters.enquiryDateFrom ? formatLocalDate(tempFilters.enquiryDateFrom) : null,
      enquiryDateTo: tempFilters.enquiryDateTo ? formatLocalDate(tempFilters.enquiryDateTo) : null,
      page: 1
    }));
    setShowDesktopFilters(false);
    setShowMobileFilters(false);
  };

  // Helper function to clear filters
  const clearFilters = () => {
    setTempFilters({
      status: '',
      userId: '',
      boreSizeId: '',
      createdById: '',
      updatedById: '',
      serviceDateFrom: null,
      serviceDateTo: null,
      enquiryDateFrom: null,
      enquiryDateTo: null
    });
    setSearchInput('');
    setDateRange([null, null]);
    setTableParams(prev => ({
      ...prev,
      search: '',
      status: '',
      userId: '',
      boreSizeId: '',
      createdById: '',
      updatedById: '',
      serviceDateFrom: null,
      serviceDateTo: null,
      enquiryDateFrom: null,
      enquiryDateTo: null,
      page: 1
    }));
  };

  // Check if any filters are active
  const hasActiveFilters = tableParams.status || tableParams.userId || tableParams.boreSizeId ||
    tableParams.createdById || tableParams.updatedById || tableParams.search ||
    tableParams.serviceDateFrom || tableParams.serviceDateTo ||
    tableParams.enquiryDateFrom || tableParams.enquiryDateTo;

  // Form handling
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    watch,
    setValue,
    trigger,
  } = useForm<EnquiryFormDataWithEdit>({
    resolver: yupResolver(enquirySchema) as any,
    defaultValues: {
      company_name: '',
      contact_person: '',
      contact_no: '',
      street: '',
      town: '',
      district: ''
    }
  });

  // Load lists for dropdowns
  const loadLists = useCallback(async () => {
    try {
      const promises: Promise<any>[] = [boreSizeService.getBoreSizes({ limit: 1000 }), rigService.getRigs({ limit: 1000 })];

      // Load users based on role
      if (user?.role === 'ADMIN') {
        promises.unshift(userService.getUsers({ limit: 100 }));
      }

      const responses = await Promise.all(promises);

      if (user?.role === 'ADMIN') {
        const [usersRes, boreSizesRes, rigsRes] = responses;
        const allUsers = usersRes.data || [];
        setUsers(allUsers);

        // Filter field team users (exclude managers and admins)
        setFieldTeamUsers(allUsers.filter((u: any) => u.role === 'FIELD_TEAM'));

        setBoreSizes(boreSizesRes.data || []);
        setRigs(rigsRes.data?.filter((r: any) => r.is_active === 'Y') || []);
      } else {
        const [boreSizesRes, rigsRes] = responses;
        setBoreSizes(boreSizesRes.data || []);
        setRigs(rigsRes.data?.filter((r: any) => r.is_active === 'Y') || []);
      }
    } catch (error) {
      console.error('Error loading lists:', error);
      showToast.error('Failed to load dropdown options. Please refresh the page.');
    }
  }, [user?.role]);

  // Load enquiries
  const loadEnquiries = useCallback(async (params: TableParams, sortingState: SortingState) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const sortField = sortingState[0]?.id || 'created_at';
      const sortOrder = sortingState[0]?.desc ? 'desc' : 'asc';

      const response = await enquiryService.getEnquiries({
        page: params.page,
        limit: params.limit,
        search: params.search,
        status: params.status as any,
        userId: params.userId ? parseInt(params.userId) : undefined,
        boreSizeId: params.boreSizeId ? parseInt(params.boreSizeId) : undefined,
        createdById: params.createdById ? parseInt(params.createdById) : undefined,
        updatedById: params.updatedById ? parseInt(params.updatedById) : undefined,
        serviceDateFrom: params.serviceDateFrom || undefined,
        serviceDateTo: params.serviceDateTo || undefined,
        enquiryDateFrom: params.enquiryDateFrom || undefined,
        enquiryDateTo: params.enquiryDateTo || undefined,
        sortBy: sortField as any,
        sortOrder
      });

      if (requestId !== requestIdRef.current) return; // stale response, discard
      setEnquiries(response.enquiries);
      setTotalEnquiries(response.pagination.total);
      setTotalPages(response.pagination.pages);
    } catch (error: any) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading enquiries:', error);
      showToast.error(error.response?.data?.message || 'Failed to load enquiries');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Effect for role-based access control
  useEffect(() => {
    // Check if user has permission to access this page
    if (user && !['ADMIN', 'MANAGER', 'FIELD_TEAM'].includes(user.role)) {
      // Redirect to dashboard or show unauthorized message
      showToast.error('You do not have permission to access this page');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Effect for initial load
  useEffect(() => {
    // Only load data if user has permission
    if (user && (user.role === 'ADMIN' || user.role === 'MANAGER')) {
      loadEnquiries(tableParams, sorting);
      loadLists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect for debounced search
  useEffect(() => {
    setTableParams(prev => ({
      ...prev,
      search: debouncedSearch,
      page: 1
    }));
  }, [debouncedSearch]);

  // Effect for table params or sorting changes
  useEffect(() => {
    loadEnquiries(tableParams, sorting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParams, sorting]);

  // Effect for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEnquiry(null);
    reset();
  };

  const handleCreate = () => {
    reset({
      company_name: '',
      contact_person: '',
      contact_no: '',
      street: '',
      town: '',
      district: '',
      service_date: '',
      bore_size_id: undefined,
      user_id: user?.role === 'MANAGER' ? user.id : undefined, // Auto-assign to manager
      assign_field_team: undefined,
      rig_id: undefined,
      pipe_rate: undefined,
      pipe_rate_alt: undefined,
      batta: undefined
    });
    setEditingEnquiry(null);
    setShowModal(true);
  };

  const handleEdit = useCallback((enquiry: Enquiry) => {
    reset({
      company_name: enquiry.company_name || '',
      contact_person: enquiry.contact_person || '',
      contact_no: enquiry.contact_no || '',
      street: enquiry.street || '',
      town: enquiry.town || '',
      district: enquiry.district || '',
      service_date: enquiry.service_date ? new Date(enquiry.service_date).toISOString().split('T')[0] : '',
      bore_size_id: enquiry.bore_size_id || undefined,
      user_id: enquiry.user_id || undefined,
      assign_field_team: enquiry.assign_field_team || undefined,
      rig_id: enquiry.rig_id || undefined,
      pipe_rate: enquiry.pipe_rate || undefined,
      pipe_rate_alt: enquiry.pipe_rate_alt || undefined,
      batta: enquiry.batta || undefined,
      location_coordinates: enquiry.location_coordinates || '',
      location_url: enquiry.location_url || '',
      isEdit: true
    });
    setEditingEnquiry(enquiry);
    setShowModal(true);
  }, [reset]);

  const handleDelete = (enquiry: Enquiry) => {
    setDeleteConfirm({ show: true, enquiry });
  };

  const handleViewDetails = (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowDetailSidebar(true);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.enquiry) return;

    setDeleting(true);
    try {
      await enquiryService.deleteEnquiry(deleteConfirm.enquiry.id);
      showToast.success('Enquiry deleted successfully');

      // Close dialog and reload data immediately
      setDeleteConfirm({ show: false, enquiry: null });
      loadEnquiries(tableParams, sorting);
    } catch (error: any) {
      console.error('Error deleting enquiry:', error);
      showToast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to delete enquiry');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Build query params from current filters
      const params = new URLSearchParams();
      if (tableParams.search) params.append('search', tableParams.search);
      if (tableParams.status) params.append('status', tableParams.status);
      if (tableParams.userId) params.append('userId', tableParams.userId);
      if (tableParams.boreSizeId) params.append('boreSizeId', tableParams.boreSizeId);
      if (tableParams.createdById) params.append('createdById', tableParams.createdById);
      if (tableParams.updatedById) params.append('updatedById', tableParams.updatedById);
      if (tableParams.serviceDateFrom) params.append('serviceDateFrom', tableParams.serviceDateFrom);
      if (tableParams.serviceDateTo) params.append('serviceDateTo', tableParams.serviceDateTo);
      if (tableParams.enquiryDateFrom) params.append('enquiryDateFrom', tableParams.enquiryDateFrom);
      if (tableParams.enquiryDateTo) params.append('enquiryDateTo', tableParams.enquiryDateTo);

      const sortField = sorting[0]?.id || 'created_at';
      const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';
      params.append('sortBy', sortField);
      params.append('sortOrder', sortOrder);

      // Download file using api service with blob response
      const response = await api.get(`/enquiries/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Enquiries_Export.xlsx';
      if (contentDisposition) {
        // Try to match filename with quotes first, then without
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=\s*"([^"]*)"|filename[^;=\n]*=\s*([^;\n]*)/i);
        if (filenameMatch) {
          filename = (filenameMatch[1] || filenameMatch[2] || '').trim();
          // Remove any remaining quotes
          filename = filename.replace(/['"]/g, '');
        }
      }

      // Ensure the filename has .xlsx extension
      if (!filename.endsWith('.xlsx')) {
        filename = filename.replace(/\.[^.]*$/, '') + '.xlsx';
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Enquiries exported successfully');
    } catch (error: any) {
      console.error('Error exporting enquiries:', error);
      showToast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to export enquiries');
    } finally {
      setExporting(false);
    }
  };



  const onSubmit = async (data: EnquiryFormDataWithEdit) => {
    const submitFunction = async (formData: EnquiryFormDataWithEdit) => {
      setSubmitting(true);
      try {
        // For managers, auto-assign to themselves
        if (user?.role === 'MANAGER') {
          formData.user_id = user.id;
        }
        
        if (editingEnquiry) {
          await enquiryService.updateEnquiry(editingEnquiry.id, formData);
          showToast.success('Enquiry updated successfully');
        } else {
          await enquiryService.createEnquiry(formData);
          showToast.success('Enquiry created successfully');
        }
        
        // Close modal and reload data immediately
        handleCloseModal();
        loadEnquiries(tableParams, sorting);
      } finally {
        setSubmitting(false);
      }
    };

    try {
      await protectedSubmit(data, submitFunction);
    } catch (error: any) {
      console.error('Form submission error:', error);
      
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        Object.keys(backendErrors).forEach((field) => {
          setError(field as any, {
            type: 'server',
            message: backendErrors[field].msg || backendErrors[field].message || backendErrors[field]
          });
        });
      } else {
        showToast.error(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save enquiry');
      }
      
      setSubmitting(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ enquiry }: { enquiry: Enquiry }) => {
    if (enquiry.is_cancelled === 'Y') {
      return <Badge variant="error">Cancelled</Badge>;
    } else if (enquiry.is_confirmed === 'Y') {
      return <Badge variant="success">Confirmed</Badge>;
    } else if (enquiry.location_url) {
      return <Badge variant="info">Site Visited</Badge>;
    } else {
      return <Badge variant="warning">Pending</Badge>;
    }
  };

  // Table columns
  const columns = useMemo<ColumnDef<Enquiry>[]>(() => [
    {
      accessorKey: 'sno',
      header: 'S.No',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-sm font-medium text-gray-900">
          {((tableParams.page - 1) * tableParams.limit) + row.index + 1}
        </div>
      ),
    },
    {
      accessorKey: 'enquiry_no',
      header: 'Enquiry No',
      cell: ({ row }) => (
        <div className="font-mono text-xs">{row.original.enquiry_no || 'N/A'}</div>
      ),
    },
    {
      accessorKey: 'company_name',
      header: 'Customer & Contact',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">{row.original.contact_person || 'N/A'}</div>
          {row.original.company_name && (
            <div className="text-xs text-gray-500">{row.original.company_name}</div>
          )}
          {row.original.contact_no && (
            <div className="flex items-center gap-1 mt-1">
              <PhoneIcon className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-600">{row.original.contact_no}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'location',
      header: 'Location',
      cell: ({ row }) => {
        const location = [row.original.town, row.original.district]
          .filter(Boolean)
          .join(', ');
        return (
          <div className="flex items-center gap-1">
            <MapPinIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm">{location || 'N/A'}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Enquiry Date',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <CalendarIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm">
            {row.original.created_at 
              ? new Date(row.original.created_at).toLocaleDateString()
              : 'N/A'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'service_date',
      header: 'Service Date',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <CalendarIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm">
            {row.original.service_date 
              ? new Date(row.original.service_date).toLocaleDateString()
              : 'Not scheduled'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'boreSize',
      header: 'Bore Size',
      cell: ({ row }) => (
        <Badge variant="primary">{row.original.boreSize?.size || 'N/A'}</Badge>
      ),
    },
    {
      accessorKey: 'assignDetails',
      header: 'Assign Details',
      cell: ({ row }) => (
        <div className="text-xs">
          {row.original.rig && (
            <div className="text-gray-600 mb-0.5">
              <span className="font-medium">Rig:</span> {row.original.rig.rig_no}
            </div>
          )}
          {row.original.assignedUser && (
            <div className="text-gray-600 mb-0.5">
              <span className="font-medium">Mgr:</span> {row.original.assignedUser.name}
            </div>
          )}
          {row.original.assignedFieldTeam && (
            <div className="text-gray-600">
              <span className="font-medium">Field:</span> {row.original.assignedFieldTeam.name}
            </div>
          )}
          {!row.original.rig && !row.original.assignedUser && !row.original.assignedFieldTeam && (
            <span className="text-gray-400">Not assigned</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'createdByUser',
      header: 'Created By',
      cell: ({ row }) => (
        <div className="text-xs">
          <div className="text-gray-900">{row.original.createdByUser?.name || 'N/A'}</div>
          {row.original.created_at && (
            <div className="text-gray-500 text-xs">
              {new Date(row.original.created_at).toLocaleDateString()}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'updatedByUser',
      header: 'Modified By',
      cell: ({ row }) => (
        <div className="text-xs">
          {row.original.updatedByUser ? (
            <>
              <div className="text-gray-900">{row.original.updatedByUser.name}</div>
              {row.original.updated_at && (
                <div className="text-gray-500 text-xs">
                  {new Date(row.original.updated_at).toLocaleDateString()}
                </div>
              )}
            </>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge enquiry={row.original} />,
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            size="xs"
            variant="ghost"
            onClick={() => handleViewDetails(row.original)}
            title="View details"
          >
            <EyeIcon className="w-4 h-4" />
          </Button>
          {user?.role !== 'FIELD_TEAM' && (
            <Button
              size="xs"
              variant="ghost"
              onClick={() => handleEdit(row.original)}
            >
              <PencilIcon className="w-4 h-4" />
            </Button>
          )}
          {user?.role === 'ADMIN' && (
            <Button
              size="xs"
              variant="ghost"
              onClick={() => handleDelete(row.original)}
            >
              <TrashIcon className="w-4 h-4 text-red-600" />
            </Button>
          )}
        </div>
      ),
    },
  ], [user, tableParams.page, tableParams.limit, handleEdit]);

  // Status options for filter
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'site_visited', label: 'Site Visited' }
  ];

  // Memoized assignment options to prevent recreation on every render (Manager and Admin)
  const assignmentOptions = useMemo(() => {
    return users
      .filter(u => u.role === 'MANAGER' || u.role === 'ADMIN')
      .map(u => ({ 
        value: u.id.toString(), 
        label: `${u.name} (${u.mobile}) - ${u.role}` 
      }));
  }, [users]);

  // Memoized field team options (FIELD_TEAM role only)
  const fieldTeamOptions = useMemo(() => {
    return fieldTeamUsers.map(u => ({ 
      value: u.id.toString(), 
      label: `${u.name} (${u.mobile})` 
    }));
  }, [fieldTeamUsers]);

  // Check for authorization before rendering
  if (user && !['ADMIN', 'MANAGER', 'FIELD_TEAM'].includes(user.role)) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">You do not have permission to access this page.</p>
            <p className="text-sm text-gray-500">You do not have access to Enquiries Management.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="h-full flex flex-col relative">
        {/* Header */}
        {/* Mobile Header */}
        <div className="flex-shrink-0 mb-6">
          {/* Mobile: Compact header with inline actions */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">Enquiries</h1>
                {totalEnquiries > 0 && (
                  <p className="text-xs text-gray-500">
                    {totalEnquiries} total
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 ml-3">
                <Button
                  onClick={handleExport}
                  size="sm"
                  variant="outline"
                  className="p-2"
                  loading={exporting}
                  title="Export to Excel"
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                </Button>
                <div className="relative">
                  <Button
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    size="sm"
                    variant={showMobileFilters ? 'primary' : 'outline'}
                    className="p-2"
                  >
                    {showMobileFilters ? <XMarkIcon className="w-5 h-5" /> : <FunnelIcon className="w-5 h-5" />}
                  </Button>
                  {/* Filter indicator */}
                  {hasActiveFilters && !showMobileFilters && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                  )}
                </div>
                {user?.role !== 'FIELD_TEAM' && (
                  <Button
                    onClick={handleCreate}
                    size="sm"
                    className="p-2"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Desktop: Original layout */}
          <div className="hidden sm:flex sm:justify-between sm:items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Enquiries Management</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
                Manage customer enquiries and bore requests
                {totalEnquiries > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({totalEnquiries} total enquiries)
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-3">
              {/* Export Button */}
              <Button
                onClick={handleExport}
                size="lg"
                variant="outline"
                leftIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
                loading={exporting}
              >
                Export
              </Button>
              {/* Filter Button */}
              <div className="relative">
                <Button
                  onClick={() => setShowDesktopFilters(!showDesktopFilters)}
                  size="lg"
                  variant={showDesktopFilters ? 'primary' : 'outline'}
                  leftIcon={<FunnelIcon className="w-5 h-5" />}
                >
                  Filters
                  {hasActiveFilters && !showDesktopFilters && (
                    <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                      {[tableParams.status, tableParams.userId, tableParams.boreSizeId, tableParams.createdById, tableParams.updatedById, tableParams.serviceDateFrom, tableParams.serviceDateTo, tableParams.enquiryDateFrom, tableParams.enquiryDateTo].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </div>
              {user?.role !== 'FIELD_TEAM' && (
                <Button
                  onClick={handleCreate}
                  size="lg"
                  leftIcon={<PlusIcon className="w-5 h-5" />}
                >
                  New Enquiry
                </Button>
              )}
            </div>
          </div>
        </div>


        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mb-4 bg-white p-4 rounded-2xl shadow-medium border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Active Filters:</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <XMarkIcon className="w-4 h-4" />
                Clear All
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Search Filter */}
              {tableParams.search && (
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Search:</span>
                  <span>{tableParams.search}</span>
                  <button
                    onClick={() => {
                      setSearchInput('');
                      setTableParams(prev => ({ ...prev, search: '', page: 1 }));
                    }}
                    className="hover:bg-blue-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Status Filter */}
              {tableParams.status && (
                <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Status:</span>
                  <span>{statusOptions.find(opt => opt.value === tableParams.status)?.label}</span>
                  <button
                    onClick={() => {
                      setTempFilters(prev => ({ ...prev, status: '' }));
                      setTableParams(prev => ({ ...prev, status: '', page: 1 }));
                    }}
                    className="hover:bg-purple-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Assigned User Filter */}
              {tableParams.userId && (
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Assigned:</span>
                  <span>{users.find(u => u.id.toString() === tableParams.userId)?.name}</span>
                  <button
                    onClick={() => {
                      setTempFilters(prev => ({ ...prev, userId: '' }));
                      setTableParams(prev => ({ ...prev, userId: '', page: 1 }));
                    }}
                    className="hover:bg-green-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Created By Filter */}
              {tableParams.createdById && (
                <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Created By:</span>
                  <span>{users.find(u => u.id.toString() === tableParams.createdById)?.name}</span>
                  <button
                    onClick={() => {
                      setTempFilters(prev => ({ ...prev, createdById: '' }));
                      setTableParams(prev => ({ ...prev, createdById: '', page: 1 }));
                    }}
                    className="hover:bg-yellow-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Modified By Filter */}
              {tableParams.updatedById && (
                <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Modified By:</span>
                  <span>{users.find(u => u.id.toString() === tableParams.updatedById)?.name}</span>
                  <button
                    onClick={() => {
                      setTempFilters(prev => ({ ...prev, updatedById: '' }));
                      setTableParams(prev => ({ ...prev, updatedById: '', page: 1 }));
                    }}
                    className="hover:bg-orange-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Bore Size Filter */}
              {tableParams.boreSizeId && (
                <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Bore Size:</span>
                  <span>{boreSizes.find(bs => bs.id.toString() === tableParams.boreSizeId)?.size}</span>
                  <button
                    onClick={() => {
                      setTempFilters(prev => ({ ...prev, boreSizeId: '' }));
                      setTableParams(prev => ({ ...prev, boreSizeId: '', page: 1 }));
                    }}
                    className="hover:bg-indigo-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Service Date Filter */}
              {(tableParams.serviceDateFrom || tableParams.serviceDateTo) && (
                <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Service Date:</span>
                  <span>
                    {tableParams.serviceDateFrom && new Date(tableParams.serviceDateFrom).toLocaleDateString()}
                    {tableParams.serviceDateFrom && tableParams.serviceDateTo && ' - '}
                    {tableParams.serviceDateTo && new Date(tableParams.serviceDateTo).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => {
                      setTempFilters(prev => ({ ...prev, serviceDateFrom: null, serviceDateTo: null }));
                      setTableParams(prev => ({ ...prev, serviceDateFrom: null, serviceDateTo: null, page: 1 }));
                    }}
                    className="hover:bg-emerald-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Enquiry Date Filter */}
              {(tableParams.enquiryDateFrom || tableParams.enquiryDateTo) && (
                <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-800 px-3 py-1.5 rounded-lg text-sm">
                  <span className="font-medium">Enquiry Date:</span>
                  <span>
                    {tableParams.enquiryDateFrom && new Date(tableParams.enquiryDateFrom).toLocaleDateString()}
                    {tableParams.enquiryDateFrom && tableParams.enquiryDateTo && ' - '}
                    {tableParams.enquiryDateTo && new Date(tableParams.enquiryDateTo).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => {
                      setTempFilters(prev => ({ ...prev, enquiryDateFrom: null, enquiryDateTo: null }));
                      setTableParams(prev => ({ ...prev, enquiryDateFrom: null, enquiryDateTo: null, page: 1 }));
                    }}
                    className="hover:bg-violet-200 rounded p-0.5"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filter Sidebar - Mobile & Desktop */}
        <>
          {(showMobileFilters || showDesktopFilters) && (
            <>
              {/* Backdrop overlay */}
              <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => {
                  setShowMobileFilters(false);
                  setShowDesktopFilters(false);
                }}
              />

              {/* Filter sidebar */}
              <div
                className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white z-50 shadow-xl flex flex-col"
              >
                {/* Sidebar header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <FunnelIcon className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                    {hasActiveFilters && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                        {[tableParams.status, tableParams.userId, tableParams.boreSizeId, tableParams.createdById, tableParams.updatedById, tableParams.serviceDateFrom, tableParams.serviceDateTo, tableParams.enquiryDateFrom, tableParams.enquiryDateTo].filter(Boolean).length}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowMobileFilters(false);
                      setShowDesktopFilters(false);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Filter content */}
                <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                  {/* Search */}
                  <Input
                    label="Search"
                    placeholder="Search enquiries..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  
                  {/* Status */}
                  <Select
                    label="Status"
                    placeholder="All Status"
                    value={statusOptions.find(opt => opt.value === tempFilters.status) || null}
                    onChange={(selectedOption: any) => setTempFilters(prev => ({ ...prev, status: selectedOption?.value || '' }))}
                    options={statusOptions}
                    isClearable
                    usePortal={false}
                  />
                  
                  {/* Assigned User (Admin only) */}
                  {user?.role === 'ADMIN' && (
                    <Select
                      label="Assigned User"
                      placeholder="All Managers & Admins"
                      value={(() => {
                        const selectedUser = users.find(u => u.id.toString() === tempFilters.userId && (u.role === 'MANAGER' || u.role === 'ADMIN'));
                        return selectedUser ? {
                          value: tempFilters.userId,
                          label: `${selectedUser.name} (${selectedUser.mobile}) - ${selectedUser.role}`
                        } : null;
                      })()}
                      onChange={(selectedOption: any) => setTempFilters(prev => ({ ...prev, userId: selectedOption?.value || '' }))}
                      options={assignmentOptions}
                      isSearchable
                      isClearable
                      usePortal={false}
                    />
                  )}

                  {/* Created By (Admin only) */}
                  {user?.role === 'ADMIN' && (
                    <Select
                      label="Created By"
                      placeholder="All Users"
                      value={(() => {
                        const selectedUser = users.find(u => u.id.toString() === tempFilters.createdById);
                        return selectedUser ? {
                          value: tempFilters.createdById,
                          label: selectedUser.name
                        } : null;
                      })()}
                      onChange={(selectedOption: any) => setTempFilters(prev => ({ ...prev, createdById: selectedOption?.value || '' }))}
                      options={users.map(u => ({ value: u.id.toString(), label: u.name }))}
                      isSearchable
                      isClearable
                      usePortal={false}
                    />
                  )}

                  {/* Modified By (Admin only) */}
                  {user?.role === 'ADMIN' && (
                    <Select
                      label="Modified By"
                      placeholder="All Users"
                      value={(() => {
                        const selectedUser = users.find(u => u.id.toString() === tempFilters.updatedById);
                        return selectedUser ? {
                          value: tempFilters.updatedById,
                          label: selectedUser.name
                        } : null;
                      })()}
                      onChange={(selectedOption: any) => setTempFilters(prev => ({ ...prev, updatedById: selectedOption?.value || '' }))}
                      options={users.map(u => ({ value: u.id.toString(), label: u.name }))}
                      isSearchable
                      isClearable
                      usePortal={false}
                    />
                  )}

                  {/* Bore Size */}
                  <Select
                    label="Bore Size"
                    placeholder="All Sizes"
                    value={boreSizes.find(bs => bs.id.toString() === tempFilters.boreSizeId) ? { value: tempFilters.boreSizeId, label: boreSizes.find(bs => bs.id.toString() === tempFilters.boreSizeId)?.size } : null}
                    onChange={(selectedOption: any) => setTempFilters(prev => ({ ...prev, boreSizeId: selectedOption?.value || '' }))}
                    options={boreSizes.map(bs => ({ value: bs.id.toString(), label: bs.size }))}
                    isClearable
                    usePortal={false}
                  />

                  {/* Service Date Range */}
                  <DateRangePicker
                    label="Service Date Range"
                    startDate={tempFilters.serviceDateFrom}
                    endDate={tempFilters.serviceDateTo}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setTempFilters(prev => ({
                        ...prev,
                        serviceDateFrom: start,
                        serviceDateTo: end
                      }));
                    }}
                    placeholder="Filter by service date"
                    usePortal={false}
                  />

                  {/* Enquiry Date Range */}
                  <DateRangePicker
                    label="Enquiry Date Range"
                    startDate={tempFilters.enquiryDateFrom}
                    endDate={tempFilters.enquiryDateTo}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setTempFilters(prev => ({
                        ...prev,
                        enquiryDateFrom: start,
                        enquiryDateTo: end
                      }));
                    }}
                    placeholder="Filter by enquiry date"
                    usePortal={false}
                  />
                </div>
                
                {/* Sidebar footer with action buttons */}
                <div className="p-4 border-t border-gray-200 space-y-3">
                  <Button
                    onClick={() => {
                      setTempFilters({
                        status: '',
                        userId: '',
                        boreSizeId: '',
                        createdById: '',
                        updatedById: '',
                        serviceDateFrom: null,
                        serviceDateTo: null,
                        enquiryDateFrom: null,
                        enquiryDateTo: null
                      });
                    }}
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applyFilters}
                    className="w-full"
                    size="lg"
                    leftIcon={<FunnelIcon className="w-4 h-4" />}
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </>
          )}
        </>

        {/* Data Table for Desktop */}
        {!isMobile ? (
          <div className="flex-1 min-h-0 bg-white rounded-2xl shadow-medium border border-gray-200 flex flex-col">
            <DataTable
              data={enquiries}
              columns={columns}
              loading={loading}
              sorting={sorting}
              onSortingChange={setSorting}
              showPagination={false}
              searchable={false}
            />
          </div>
        ) : (
          // Mobile Cards View
          <div className="flex-1 min-h-0 flex flex-col gap-4 relative">
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
                <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-strong border border-gray-200">
                  <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-gray-600 text-sm font-medium mt-3">Loading enquiries...</p>
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="divide-y divide-gray-100">
                  {enquiries.map((enquiry, index) => (
                    <div
                      key={enquiry.id}
                      className="bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => handleViewDetails(enquiry)}
                    >
                      <div className="px-4 py-3">
                        <div className="flex items-start space-x-3">
                          {/* Compact number indicator */}
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 mt-0.5">
                            {((tableParams.page - 1) * tableParams.limit) + index + 1}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {/* Main content */}
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                {/* Primary info */}
                                <div className="font-medium text-gray-900 truncate">
                                  {enquiry.contact_person || 'N/A'}
                                </div>
                                
                                {/* Secondary info line */}
                                <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
                                  <span className="truncate">
                                    {enquiry.company_name || enquiry.contact_no}
                                  </span>
                                  {enquiry.boreSize && (
                                    <>
                                      <span>•</span>
                                      <span>{enquiry.boreSize.size}</span>
                                    </>
                                  )}
                                </div>

                                {/* Dates row - separate line */}
                                <div className="flex items-center space-x-3 mt-1">
                                  {enquiry.created_at && (
                                    <div className="flex items-center space-x-1">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                      <span className="text-xs text-gray-600">
                                        {new Date(enquiry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {enquiry.service_date && (
                                    <div className="flex items-center space-x-1">
                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                      <span className="text-xs text-gray-600">
                                        {new Date(enquiry.service_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Status and actions */}
                              <div className="flex flex-col items-end space-y-2 ml-3">
                                <Badge
                                  variant={
                                    enquiry.is_cancelled === 'Y' ? 'error' :
                                    enquiry.is_confirmed === 'Y' ? 'success' :
                                    enquiry.location_url ? 'info' : 'warning'
                                  }
                                  size="sm"
                                >
                                  {enquiry.is_cancelled === 'Y' ? 'Cancel' :
                                   enquiry.is_confirmed === 'Y' ? 'Done' :
                                   enquiry.location_url ? 'Visited' : 'Pending'}
                                </Badge>
                                
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowActionMenu(showActionMenu === enquiry.id ? null : enquiry.id);
                                    }}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                  >
                                    <EllipsisVerticalIcon className="w-4 h-4 text-gray-400" />
                                  </button>
                                  
                                  {showActionMenu === enquiry.id && (
                                    <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                      {user?.role !== 'FIELD_TEAM' && (
                                        <button
                                          onClick={() => {
                                            handleEdit(enquiry);
                                            setShowActionMenu(null);
                                          }}
                                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                        >
                                          <PencilIcon className="w-3 h-3 mr-2" />
                                          Edit
                                        </button>
                                      )}
                                      {user?.role === 'ADMIN' && (
                                        <button
                                          onClick={() => {
                                            handleDelete(enquiry);
                                            setShowActionMenu(null);
                                          }}
                                          className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                                        >
                                          <TrashIcon className="w-3 h-3 mr-2" />
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {enquiries.length === 0 && !loading && (
                    <div className="text-center py-12">
                      <div className="text-gray-500 text-lg">No enquiries found</div>
                      <div className="text-gray-400 text-sm mt-2">Try adjusting your search or filter criteria</div>
                    </div>
                  )}
                </div>
            </div>
          </div>
        )}

        {/* Custom Pagination */}
        {!isMobile && (
          <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-gray-200 bg-white">{/* Add pagination content for desktop after the DataTable */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-700 whitespace-nowrap">
              Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
              {Math.min(tableParams.page * tableParams.limit, totalEnquiries)} of{' '}
              {totalEnquiries} results
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="w-full sm:w-auto">
                <Select
                  options={[
                    { value: '5', label: '5 per page' },
                    { value: '10', label: '10 per page' },
                    { value: '25', label: '25 per page' },
                    { value: '50', label: '50 per page' }
                  ]}
                  value={{ value: tableParams.limit.toString(), label: `${tableParams.limit} per page` }}
                  onChange={(option: any) => setTableParams(prev => ({ ...prev, limit: parseInt(option?.value), page: 1 }))}
                  usePortal={false}
                  menuPlacement="top"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTableParams(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={tableParams.page === 1}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + Math.max(1, tableParams.page - 2);
                    if (pageNum > totalPages) return null;
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={pageNum === tableParams.page ? 'primary' : 'ghost'}
                        onClick={() => setTableParams(prev => ({ ...prev, page: pageNum }))}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTableParams(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={tableParams.page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
          </div>
        )}
        
        {/* Mobile Pagination */}
        {isMobile && (
          <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex items-center justify-between gap-2">
              {/* Per page selector on left */}
              <div className="w-20">
                <Select
                  options={[
                    { value: '10', label: '10' },
                    { value: '15', label: '15' },
                    { value: '20', label: '20' },
                    { value: '30', label: '30' },
                    { value: '50', label: '50' }
                  ]}
                  value={{ value: tableParams.limit.toString(), label: tableParams.limit.toString() }}
                  onChange={(option: any) => setTableParams(prev => ({ ...prev, limit: parseInt(option?.value), page: 1 }))}
                  usePortal={false}
                  menuPlacement="top"
                  isSearchable={false}
                />
              </div>

              {/* Navigation on right */}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTableParams(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={tableParams.page === 1}
                  className="px-3 h-8"
                >
                  ←
                </Button>

                <div className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">
                  Page {tableParams.page} of {totalPages}
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTableParams(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={tableParams.page === totalPages}
                  className="px-3 h-8"
                >
                  →
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingEnquiry ? 'Edit Enquiry' : 'New Enquiry'}
          size="lg"
          footer={
            <>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" form="enquiry-form" loading={submitting}>
                {editingEnquiry ? 'Update Enquiry' : 'Create Enquiry'}
              </Button>
            </>
          }
        >
          <form id="enquiry-form" onSubmit={handleSubmit(onSubmit as any)} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Company Name"
                {...register('company_name')}
                error={errors.company_name?.message}
              />
              <Input
                label="Contact Person *"
                {...register('contact_person')}
                error={errors.contact_person?.message}
              />
              <Input
                label="Mobile No *"
                {...register('contact_no')}
                error={errors.contact_no?.message}
              />
              <DatePicker
                label="Service Date *"
                value={watch('service_date')}
                onChange={(date) => {
                  setValue('service_date', date || '');
                  trigger('service_date');
                }}
                error={errors.service_date?.message}
                required
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700">Address</h3>
              <div className="space-y-4">
                <Input
                  label="Street"
                  {...register('street')}
                  error={errors.street?.message}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Town"
                    {...register('town')}
                    error={errors.town?.message}
                  />
                  <Input
                    label="District"
                    {...register('district')}
                    error={errors.district?.message}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Bore Size *"
                value={boreSizes.find(bs => bs.id === watch('bore_size_id')) ? { value: watch('bore_size_id')?.toString() || '', label: boreSizes.find(bs => bs.id === watch('bore_size_id'))?.size || '' } : null}
                onChange={(selectedOption: any) => {
                  setValue('bore_size_id', selectedOption ? parseInt(selectedOption.value) : undefined);
                  trigger('bore_size_id');
                }}
                options={[
                  ...boreSizes.map(bs => ({ value: bs.id.toString(), label: bs.size }))
                ]}
                error={errors.bore_size_id?.message}
                isSearchable
                isClearable
                usePortal={false}
              />
              {user?.role === 'ADMIN' && (() => {
                const selectedUserId = watch('user_id');

                // Find selected option from the memoized options array to ensure reference consistency
                const selectedValue = selectedUserId ?
                  assignmentOptions.find(option => parseInt(option.value) === selectedUserId) || null :
                  null;
                
                return (
                  <Select
                    label="Assign Manager *"
                    value={selectedValue}
                    onChange={(selectedOption: any) => {
                      console.log('Selected option:', selectedOption); // Debug log
                      const newUserId = selectedOption ? parseInt(selectedOption.value) : undefined;
                      console.log('Setting user_id to:', newUserId); // Debug log
                      
                      setValue('user_id', newUserId, { 
                        shouldValidate: true, 
                        shouldDirty: true, 
                        shouldTouch: true 
                      });
                    }}
                    options={assignmentOptions}
                    error={errors.user_id?.message}
                    isSearchable
                    isClearable
                    usePortal={false}
                    key={`assign-to-${editingEnquiry?.id || 'new'}-${selectedUserId}`} // Include selectedUserId in key
                  />
                );
              })()}
              {user?.role === 'ADMIN' && (() => {
                const selectedFieldTeamId = watch('assign_field_team');

                // Find selected option from the memoized options array
                const selectedFieldTeamValue = selectedFieldTeamId ?
                  fieldTeamOptions.find(option => parseInt(option.value) === selectedFieldTeamId) || null :
                  null;

                return (
                  <Select
                    label="Assign Field Team"
                    value={selectedFieldTeamValue}
                    onChange={(selectedOption: any) => {
                      const newFieldTeamId = selectedOption ? parseInt(selectedOption.value) : undefined;
                      setValue('assign_field_team', newFieldTeamId, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true
                      });
                    }}
                    options={fieldTeamOptions}
                    error={errors.assign_field_team?.message}
                    isSearchable
                    isClearable
                    usePortal={false}
                    key={`assign-field-team-${editingEnquiry?.id || 'new'}-${selectedFieldTeamId}`}
                  />
                );
              })()}
              {user?.role === 'ADMIN' && (() => {
                const selectedRigId = watch('rig_id');

                // Find selected option from the rigs array
                const selectedRigValue = selectedRigId ?
                  rigs.find(rig => rig.id === selectedRigId) ?
                    { value: selectedRigId.toString(), label: rigs.find(rig => rig.id === selectedRigId)?.rig_no || '' } :
                    null :
                  null;

                return (
                  <Select
                    label="Rig Master"
                    value={selectedRigValue}
                    onChange={(selectedOption: any) => {
                      const newRigId = selectedOption ? parseInt(selectedOption.value) : undefined;
                      setValue('rig_id', newRigId, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true
                      });
                    }}
                    options={rigs.map(r => ({ value: r.id.toString(), label: r.rig_no }))}
                    error={errors.rig_id?.message}
                    isSearchable
                    isClearable
                    usePortal={false}
                    key={`rig-${editingEnquiry?.id || 'new'}-${selectedRigId}`}
                  />
                );
              })()}
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700">Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Pipe Rate"
                  type="number"
                  step="0.01"
                  {...register('pipe_rate')}
                  error={errors.pipe_rate?.message}
                />
                <Input
                  label="Alt. Pipe Rate"
                  type="number"
                  step="0.01"
                  {...register('pipe_rate_alt')}
                  error={errors.pipe_rate_alt?.message}
                />
                <Input
                  label="Batta"
                  type="number"
                  step="0.01"
                  {...register('batta')}
                  error={errors.batta?.message}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700">Location Tracking</h3>
              <LocationInput
                coordinates={watch('location_coordinates') || ''}
                mapUrl={watch('location_url') || ''}
                onCoordinatesChange={(coords) => setValue('location_coordinates', coords)}
                onMapUrlChange={(url) => setValue('location_url', url)}
                disabled={submitting}
              />
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, enquiry: null })}
          onConfirm={confirmDelete}
          title="Delete Enquiry"
          message={`Are you sure you want to delete enquiry "${deleteConfirm.enquiry?.enquiry_no}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          loading={deleting}
        />

        {/* Detail Sidebar */}
        <>
          {showDetailSidebar && selectedEnquiry && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/20 z-40"
                onClick={() => setShowDetailSidebar(false)}
              />

              {/* Sidebar */}
              <div
                className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
              >
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Enquiry Details</h2>
                    <button
                      onClick={() => setShowDetailSidebar(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="mt-2">
                    <div className="text-sm font-mono text-gray-500">{selectedEnquiry.enquiry_no}</div>
                    <div className="mt-1">
                      <Badge 
                        variant={
                          selectedEnquiry.is_confirmed === 'Y' ? 'success' : 
                          selectedEnquiry.is_cancelled === 'Y' ? 'error' : 'warning'
                        }
                      >
                        {selectedEnquiry.is_confirmed === 'Y' ? 'Confirmed' : 
                         selectedEnquiry.is_cancelled === 'Y' ? 'Cancelled' : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Customer Info */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h3>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-500">Contact Person</label>
                        <div className="font-medium text-gray-900">{selectedEnquiry.contact_person || 'N/A'}</div>
                      </div>
                      {selectedEnquiry.company_name && (
                        <div>
                          <label className="text-xs text-gray-500">Company</label>
                          <div className="text-gray-900">{selectedEnquiry.company_name}</div>
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-gray-500">Mobile</label>
                        <div className="font-mono text-gray-900">{selectedEnquiry.contact_no || 'N/A'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Location</h3>
                    <div className="space-y-1">
                      {selectedEnquiry.street && (
                        <div className="text-gray-900">{selectedEnquiry.street}</div>
                      )}
                      <div className="text-gray-900">
                        {[selectedEnquiry.town, selectedEnquiry.district].filter(Boolean).join(', ') || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Important Dates</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <span className="text-sm text-gray-600">Enquiry Date</span>
                        </div>
                        <div className="text-sm font-medium">
                          {selectedEnquiry.created_at ? new Date(selectedEnquiry.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-gray-600">Service Date</span>
                        </div>
                        <div className="text-sm font-medium">
                          {selectedEnquiry.service_date ? new Date(selectedEnquiry.service_date).toLocaleDateString() : 'Not scheduled'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Technical Details */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Technical Details</h3>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-500">Bore Size</label>
                        <div className="text-gray-900">{selectedEnquiry.boreSize?.size || 'N/A'}</div>
                      </div>
                      {selectedEnquiry.assignedUser && (
                        <div>
                          <label className="text-xs text-gray-500">Assigned To</label>
                          <div className="text-gray-900">{selectedEnquiry.assignedUser.name}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing */}
                  {(selectedEnquiry.pipe_rate || selectedEnquiry.pipe_rate_alt || selectedEnquiry.batta) && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Pricing</h3>
                      <div className="space-y-2">
                        {selectedEnquiry.pipe_rate && (
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-500">Pipe Rate</span>
                            <span className="font-medium">₹{selectedEnquiry.pipe_rate}</span>
                          </div>
                        )}
                        {selectedEnquiry.pipe_rate_alt && (
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-500">Pipe Rate Alt</span>
                            <span className="font-medium">₹{selectedEnquiry.pipe_rate_alt}</span>
                          </div>
                        )}
                        {selectedEnquiry.batta && (
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-500">Batta</span>
                            <span className="font-medium">₹{selectedEnquiry.batta}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {(selectedEnquiry.location_coordinates || selectedEnquiry.location_url) && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Location</h3>
                      <LocationDisplay 
                        coordinates={selectedEnquiry.location_coordinates}
                        mapUrl={selectedEnquiry.location_url}
                      />
                    </div>
                  )}

                  {/* Actions */}
                  {user?.role !== 'FIELD_TEAM' && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            handleEdit(selectedEnquiry);
                            setShowDetailSidebar(false);
                          }}
                          size="sm"
                          className="flex-1"
                          leftIcon={<PencilIcon className="w-4 h-4" />}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      </div>
    </MainLayout>
  );
};

export default EnquiriesManagement;